import Link from "next/link"
import { BookOpen, Heart, ExternalLink } from "lucide-react"
import { LaunchBadges } from "@/components/launch-badges"

interface FooterProps {
  user?: any
}

export function Footer({ user }: FooterProps = {}) {
  return (
    <footer className="border-t bg-card/50 mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Project Info */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-primary" />
              <span className="font-semibold text-lg">Planami</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              The most customizable yet simple online planner, built for people who love the feeling of paper but crave the flexibility of digital.
            </p>
          </div>

          {/* Fast Links */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-2 flex flex-col">
              <li>
                <Link href="/yearly-reflect" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Last Year Reflection
                </Link>
              </li>
              <li>
                <Link href="/digital-planner" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Digital Planner
                </Link>
              </li>
              <li>
                <Link href="/goal-planner" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Goal Planner
                </Link>
              </li>
              <li>
                <Link href="/yearly-reflection-template" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Yearly Reflection Template
                </Link>
              </li>
              <li>
                <Link href="/notion-planner-alternative" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Notion Alternative
                </Link>
              </li>
              <li>
                <Link href="/#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Features
                </Link>
              </li>
              <li>
                <Link href="/#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Pricing
                </Link>
              </li>
              {!user && (
                <li>
                  <Link href="/auth/sign-up" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Get Started
                  </Link>
                </li>
              )}
              {user && (
                <li>
                  <Link href="/planner" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Go to Planners
                  </Link>
                </li>
              )}
              <li>
                <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm uppercase tracking-wider">Legal</h3>
            <ul className="space-y-2 flex flex-col">
              <li>
                <Link href="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/terms-of-service" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media & Contact */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm uppercase tracking-wider">Connect</h3>
            <div className="flex flex-col space-y-3">
              <div className="flex flex-col gap-2">
                <a
                  href="https://x.com/planamiapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                >
                  X (Twitter) Planami
                  <ExternalLink className="h-3 w-3" />
                </a>
                <a
                    href="https://x.com/maryna_klokova"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                  >
                    X (Twitter) Planami's creator
                    <ExternalLink className="h-3 w-3" />
                  </a>
                <a
                  href="https://instagram.com/planamiapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                >
                  Instagram
                  <ExternalLink className="h-3 w-3" />
                </a>
                <a
                  href="https://reddit.com/r/planami"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                >
                  Reddit Community
                  <ExternalLink className="h-3 w-3" />
                </a>
               {/* <a
                  href="https://tiktok.com/@littlebusywoman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                >
                  TikTok
                  <ExternalLink className="h-3 w-3" />
                </a> */}
              </div>
            </div>
          </div>
        </div>

        {/* Other products by the maker (dofollow: no rel="nofollow") */}
        <div className="border-t pt-6 pb-6">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <span>More from the maker:</span>
            {[
              { domain: "yellowcrab.ai", icon: "/maker-icons/yellowcrab.png" },
              { domain: "screenrecords.app", icon: "/maker-icons/screenrecords.png" },
              { domain: "macliphistory.com", icon: "/maker-icons/macliphistory.svg" },
              { domain: "howlaunch.com", icon: "/maker-icons/howlaunch.svg" },
            ].map(({ domain, icon }) => (
              <a
                key={domain}
                href={`https://${domain}`}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors underline-offset-4 hover:underline"
              >
                <img src={icon} alt="" width={16} height={16} className="h-4 w-4 rounded-sm" loading="lazy" />
                {domain}
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Planami. All rights reserved.
            </p>
            <LaunchBadges />
            <p className="text-xs text-muted-foreground">
              Made with <Heart className="h-3 w-3 inline text-red-500" /> by{" "}
              <span className="font-medium">planner lover</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
